import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  getSubDepartmentsList,
  createDepartment,
} from "../../../../services/departments";

const SubDepartmentsContext = createContext();

const initialState = {
  subDepartments: [],
  meta: null,
  loading: false,
  creating: false,
  error: null,
};

export const SubDepartmentsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);
  const createRequestIdRef = useRef(0);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchSubDepartments = useCallback(
    async (parentDepartmentUuid, params = {}) => {
      if (!parentDepartmentUuid) {
        updateState({ error: new Error("Parent department UUID is required") });
        return;
      }

      const requestId = ++fetchRequestIdRef.current;

      updateState({ loading: true, error: null });

      try {
        const response = await getSubDepartmentsList({
          department_uuid: parentDepartmentUuid,
          page: params.page || 1,
          page_size: params.page_size || 20,
          order_by: params.order_by || "created_at",
          order_direction: params.order_direction || "DESC",
          search: params.search || null,
        });

        if (requestId !== fetchRequestIdRef.current) {
          updateState({ loading: false });
          return;
        }

        const statusCode = response?.status_code || response?.statusCode;
        const isValidResponse = statusCode >= 200 && statusCode < 300;

        if (!isValidResponse) {
          const errorMessage =
            response?.msg_code?.[0]?.description ||
            response?.msgCode?.[0]?.description ||
            "Invalid response status code";
          throw new Error(errorMessage);
        }

        const body = response?.body || [];

        updateState({
          subDepartments: Array.isArray(body) ? body : [],
          meta: response.meta || null,
          loading: false,
          error: null,
        });
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          updateState({ loading: false });
          return;
        }

        console.error("Error fetching sub-departments:", error);
        updateState({
          loading: false,
          error,
          subDepartments: [],
          meta: null,
        });
      }
    },
    [updateState]
  );

  const createSubDepartment = useCallback(
    async (parentDepartmentUuid, subDepartmentData) => {
      if (!parentDepartmentUuid) {
        throw new Error("Parent department UUID is required");
      }

      const requestId = ++createRequestIdRef.current;

      updateState({ creating: true, error: null });

      try {
        const response = await createDepartment({
          department_name: subDepartmentData.name,
          description: subDepartmentData.description,
          owner_uuid:
            subDepartmentData.owner_uuid || subDepartmentData.ownerUuid,
          parent_department_uuid: parentDepartmentUuid,
        });

        if (requestId !== createRequestIdRef.current) {
          updateState({ creating: false });
          return null;
        }

        const statusCode = response?.status_code || response?.statusCode;
        const isValidResponse = statusCode >= 200 && statusCode < 300;

        if (!isValidResponse) {
          const errorMessage =
            response?.msg_code?.[0]?.description ||
            response?.msgCode?.[0]?.description ||
            "Failed to create sub-department";
          throw new Error(errorMessage);
        }

        const createdSubDepartment = response?.body || null;

        updateState({ creating: false, error: null });

        return createdSubDepartment;
      } catch (error) {
        if (requestId !== createRequestIdRef.current) {
          updateState({ creating: false });
          return null;
        }

        console.error("Error creating sub-department:", error);
        updateState({ creating: false, error });
        throw error;
      }
    },
    [updateState]
  );

  const value = useMemo(
    () => ({
      subDepartments: state.subDepartments,
      meta: state.meta,
      loading: state.loading,
      creating: state.creating,
      error: state.error,
      fetchSubDepartments,
      createSubDepartment,
    }),
    [
      state.subDepartments,
      state.meta,
      state.loading,
      state.creating,
      state.error,
      fetchSubDepartments,
      createSubDepartment,
    ]
  );

  return (
    <SubDepartmentsContext.Provider value={value}>
      {children}
    </SubDepartmentsContext.Provider>
  );
};

export const useSubDepartments = () => {
  const context = useContext(SubDepartmentsContext);
  if (!context) {
    throw new Error(
      "useSubDepartments must be used within SubDepartmentsProvider"
    );
  }
  return context;
};
